/**
 * Utilitaires pour la validation et la gestion des fichiers uploadés
 */

const MAX_FILE_SIZE = 200 * 1024; // 200 Ko en octets

/**
 * Valide la taille d'un fichier
 * @param {File} file - Le fichier à valider
 * @returns {{ valid: boolean, error?: string, size?: string }}
 */
export const validateFileSize = (file) => {
  if (!file) {
    return { valid: false, error: "Aucun fichier sélectionné" };
  }

  const fileSize = file.size;
  const maxSize = MAX_FILE_SIZE;
  
  if (fileSize > maxSize) {
    const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Le fichier est trop volumineux (${fileSizeMB} Mo). Taille maximale autorisée : ${maxSizeMB} Mo (200 Ko)`,
      size: formatFileSize(fileSize)
    };
  }

  return {
    valid: true,
    size: formatFileSize(fileSize)
  };
};

/**
 * Formate la taille d'un fichier en format lisible
 * @param {number} bytes - Taille en octets
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "Ko", "Mo", "Go"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Valide le type de fichier
 * @param {File} file - Le fichier à valider
 * @param {string[]} allowedTypes - Types MIME autorisés (ex: ['image/jpeg', 'application/pdf'])
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (!file) {
    return { valid: false, error: "Aucun fichier sélectionné" };
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Type de fichier non autorisé. Types acceptés : ${allowedTypes.join(", ")}`
    };
  }

  return { valid: true };
};

/**
 * Valide à la fois la taille et le type
 * @param {File} file - Le fichier à valider
 * @param {string[]} allowedTypes - Types MIME autorisés
 * @returns {{ valid: boolean, error?: string, size?: string }}
 */
export const validateFile = (file, allowedTypes = []) => {
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  if (allowedTypes.length > 0) {
    const typeValidation = validateFileType(file, allowedTypes);
    if (!typeValidation.valid) {
      return typeValidation;
    }
  }

  return {
    valid: true,
    size: sizeValidation.size
  };
};

export { MAX_FILE_SIZE };

